/**
 * Optimistic UI + persistent background sync (docs/PERF.md "Optimistic UI"). Screens change local state at once
 * (zustand, persisted); every change that the server should know becomes an operation in the core sync queue
 * (`enqueueOp`: dedupe by key, payload merge), persisted in IndexedDB, flushed in the background (debounced after a
 * change, when the network comes back, when the app becomes visible, and on the retry timer) with exponential backoff.
 * Operations read the LATEST local state when they are sent, so a burst of taps costs one request.
 *
 * Kinds: progress (sparks, lessons, hearts / favourites, collections, per-profile prefs — the server answers with the
 * merged progress, merged back with core `mergeProgress`), creatures (core `mergeCreatureLists`), profile, lang,
 * drawingMeta (journal stickers / title), comment (a compliment on a shared drawing; works signed out).
 */
import { create } from 'zustand';
import { dueOps, enqueueOp, isRetryableStatus, mergeFields, nextWakeAt, normalizeQueue, settleOp, type SendResult, type SyncOp, type SyncQueue } from '@sketchling/core';
import { ApiError, api, drawingMeta, syncCreatures } from './api';
import { useApp } from '../state/store';
import { isHidden } from './power';
import { isQuiet } from './syncFlags';

/* ------------------------------------------------ persistence (IndexedDB) ------------------------------------------------ */

const DB = 'sketchling-sync';
const STORE = 'queue';
const KEY = 'ops';

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const req = indexedDB.open(DB, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}
let dbP: Promise<IDBDatabase | null> | null = null;
const db = () => (dbP ??= openDb());

async function readQueue(): Promise<SyncQueue> {
  const d = await db();
  if (!d) return [];
  return new Promise((resolve) => {
    const t = d.transaction(STORE, 'readonly');
    const r = t.objectStore(STORE).get(KEY);
    r.onsuccess = () => resolve(normalizeQueue(r.result));
    r.onerror = () => resolve([]);
  });
}
let writing: Promise<void> = Promise.resolve();
function writeQueue(q: SyncQueue): Promise<void> {
  writing = writing.then(async () => {
    const d = await db();
    if (!d) return;
    await new Promise<void>((resolve) => {
      const t = d.transaction(STORE, 'readwrite');
      t.objectStore(STORE).put(q, KEY);
      t.oncomplete = () => resolve();
      t.onerror = () => resolve();
    });
  });
  return writing;
}

/* ------------------------------------------------ state ------------------------------------------------ */

interface SyncState {
  queue: SyncQueue;
  /** A flush is running. */
  flushing: boolean;
  /** Last op dropped after permanent failure (cheats / tests). */
  lastDropped: SyncOp | null;
}
export const useSyncQueue = create<SyncState>(() => ({ queue: [], flushing: false, lastDropped: null }));
/** Pending operations (UI hint, tests). */
export const pendingSyncCount = () => useSyncQueue.getState().queue.length;

let seq = 0;
const opId = () => `${Date.now().toString(36)}-${(seq++).toString(36)}`;
const MERGE: Record<string, typeof mergeFields | undefined> = { drawingMeta: mergeFields, profile: mergeFields };

function setQueue(q: SyncQueue) {
  useSyncQueue.setState({ queue: q });
  void writeQueue(q);
}

/** Record an operation (optimistic change already applied locally) and schedule a flush. */
export function enqueueSync(kind: string, key: string, payload: unknown = null, delayMs = 800) {
  setQueue(enqueueOp(useSyncQueue.getState().queue, { id: opId(), kind, key, payload }, Date.now(), MERGE[kind]));
  schedule(delayMs);
}

/* ------------------------------------------------ senders ------------------------------------------------ */

const fail = (e: unknown): SendResult => {
  const status = e instanceof ApiError ? e.status : 0;
  return { ok: false, retry: isRetryableStatus(status), error: e instanceof Error ? `${status} ${e.message}` : String(e) };
};
const signedIn = () => !!useApp.getState().account.accessToken;
const remoteOf = (localId: string) => useApp.getState().profiles.find((p) => p.id === localId)?.remoteId;

/** Not ready to send yet (signed out / profile not on the server yet): retried later, like a transient failure. */
const notYet = (why: string): SendResult => ({ ok: false, retry: true, error: why });

const SENDERS: Record<string, (op: SyncOp) => Promise<SendResult>> = {
  async progress(op) {
    const local = op.key.slice('progress:'.length);
    const remote = remoteOf(local);
    const p = useApp.getState().progress[local];
    if (!p) return { ok: true }; // profile deleted
    if (!signedIn() || !remote) return notYet('no account / profile yet');
    const r = await api.putProgress(remote, p);
    useApp.getState().mergeRemoteProgress(local, r.progress);
    return { ok: true };
  },
  async creatures(op) {
    const local = op.key.slice('creatures:'.length);
    const remote = remoteOf(local);
    if (!signedIn() || !remote) return notYet('no account / profile yet');
    await syncCreatures(local, remote);
    return { ok: true };
  },
  async profile(op) {
    const local = op.key.slice('profile:'.length);
    const p = useApp.getState().profiles.find((x) => x.id === local);
    if (!p) return { ok: true };
    if (!signedIn()) return notYet('signed out');
    if (p.remoteId) await api.updateProfile(p.remoteId, p);
    else {
      const r = await api.createProfile(p);
      useApp.getState().updateProfile(p.id, { remoteId: r.id });
    }
    return { ok: true };
  },
  async lang(op) {
    if (!signedIn()) return { ok: true }; // the account takes the device language when it signs in
    await api.setLanguage(String(op.payload));
    return { ok: true };
  },
  async drawingMeta(op) {
    const id = op.key.slice('drawingMeta:'.length);
    const item = useApp.getState().journal.find((j) => j.id === id);
    if (!item) return { ok: true };
    if (!item.remoteId) return notYet('drawing not uploaded yet');
    await api.patchDrawing(item.remoteId, { meta: drawingMeta(item) });
    return { ok: true };
  },
  async comment(op) {
    const { shareId, body } = op.payload as { shareId: string; body: Parameters<typeof api.publicComment>[1] };
    await api.publicComment(shareId, body);
    return { ok: true };
  },
};

/* ------------------------------------------------ flushing ------------------------------------------------ */

let timer: ReturnType<typeof setTimeout> | null = null;
function schedule(delayMs: number) {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    void flushSync();
  }, Math.max(0, delayMs));
}

/** Send every due op once (sequentially, oldest first). Safe to call any time. */
export async function flushSync(): Promise<void> {
  if (useSyncQueue.getState().flushing) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  useSyncQueue.setState({ flushing: true });
  try {
    for (const op of dueOps(useSyncQueue.getState().queue, Date.now())) {
      const send = SENDERS[op.kind];
      const res: SendResult = send ? await send(op).catch(fail) : { ok: false, retry: false, error: `unknown kind ${op.kind}` };
      const { queue, dropped } = settleOp(useSyncQueue.getState().queue, op, res, Date.now(), Math.random());
      if (dropped) {
        console.warn('[sync] dropped', dropped.kind, dropped.error);
        useSyncQueue.setState({ lastDropped: dropped });
      }
      setQueue(queue);
    }
  } finally {
    useSyncQueue.setState({ flushing: false });
  }
  const wake = nextWakeAt(useSyncQueue.getState().queue);
  if (wake !== null) schedule(wake - Date.now());
}

/* ------------------------------------------------ wiring ------------------------------------------------ */

let started = false;
/**
 * Load the persisted queue, flush it, and turn local changes into operations: progress / collections / hearts of
 * every profile, living creatures. Call once at start-up.
 */
export async function startSyncQueue(): Promise<void> {
  if (started) return;
  started = true;
  const saved = await readQueue();
  const cur = useSyncQueue.getState().queue;
  // Ops enqueued before the saved queue was read win (same key = newer).
  let q = saved;
  for (const o of cur) q = enqueueOp(q, o, Date.now(), MERGE[o.kind]);
  setQueue(q);
  // Deterministic test mode (dev builds + `?mockTts=1`, remembered per tab): e2e suites rewrite the persisted store of a
  // running app; progress then syncs only through the periodic full sync, as before the queue (the queue logic is
  // unit-tested in core). Profile / language / journal / compliment operations still go through the queue.
  const testMode = import.meta.env.DEV && (() => {
    try {
      return sessionStorage.getItem('sk-mock-tts') === '1' || new URLSearchParams(location.search).has('mockTts');
    } catch {
      return false;
    }
  })();
  useApp.subscribe((s, prev) => {
    if (testMode || s.progress === prev.progress || isQuiet()) return;
    for (const id of Object.keys(s.progress)) if (s.progress[id] !== prev.progress[id]) enqueueSync('progress', `progress:${id}`, null, 4000);
  });
  void import('../components/living/creatures').then(({ useCreatureStore }) =>
    useCreatureStore.subscribe((s, prev) => {
      if (testMode || (s.creatures === prev.creatures && s.removed === prev.removed)) return;
      const ids = new Set([...s.creatures, ...prev.creatures].map((c) => c.profileId));
      for (const id of ids) enqueueSync('creatures', `creatures:${id}`, null, 3000);
    }),
  );
  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => schedule(200));
    document.addEventListener('visibilitychange', () => !isHidden() && schedule(400));
  }
  schedule(1000);
}
